import { createApi } from '@reduxjs/toolkit/query/react';

import type { GetAshOrdersResProps } from 'services/orders/orders.types';
import type { GetPrescriptionsResProps } from 'services/patientChart/patientChart.types';
import type { GetShopOrdersRes } from 'services/shop/shop.types';

import { SortOrder } from 'utils/enums';
import { baseQueryWithReauth } from 'utils/services';

/**
 * RTK Query API slice for the platform reads the AI assistant chat makes on its own behalf. The
 * greeting's order picks (`GreetingOrderPicks`) look up the patient's prescriptions, lab kits and
 * shop orders to offer "Where is my order?" buttons under the greeting.
 *
 * These reads live apart from the shop and patient-chart slices because their failures must stay
 * silent. Many patients have no shop orders, and the platform answers `/store/orders` with a 500
 * for them; on the shop pages that 500 rightly raises the server-error toast, but a patient who
 * only opened a chat has nothing at stake there. The slice is listed in `SILENT_ERROR_REDUCER_PATHS`
 * in `store/index.ts`: a failed lookup still rejects, so the picker renders nothing for it, and no
 * toast appears.
 *
 * Same requests as `patientChartApi.getPrescriptions` (default sort), `ordersApi.getAshOrders` and
 * `shopApi.getShopOrders`.
 */
export const aiAssistantChatApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getGreetingLabOrders: build.query<GetAshOrdersResProps, void>({
      query: () => ({ url: '/orders/labs' })
    }),
    getGreetingPrescriptions: build.query<GetPrescriptionsResProps, void>({
      query: () => ({
        params: { sortField: 'signedDate', sortOrder: SortOrder.Desc },
        url: '/patient-chart/prescriptions'
      })
    }),
    getGreetingShopOrders: build.query<GetShopOrdersRes, void>({
      query: () => ({ url: '/store/orders' })
    })
  }),
  // A fresh lookup per greeting: the picks describe the moment the chat opens, and a conversation
  // renders its greeting once.
  keepUnusedDataFor: 0,
  reducerPath: 'aiAssistantChatApi'
});

export const {
  useGetGreetingLabOrdersQuery,
  useGetGreetingPrescriptionsQuery,
  useGetGreetingShopOrdersQuery
} = aiAssistantChatApi;
